import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState, type FormEvent, type MouseEvent } from 'react'
import { ApiError, type PostValue } from '../../api/client'
import {
  consumingQuery,
  isSaveenergeeSettingsTab,
  post,
  profileQuery,
  profilesQuery,
  saveenergeeSettingsShellQuery,
  type ProfileSheet,
  type SaveenergeeSettingsTab,
} from '../../api/settingsSaveenergee'
import { formFields } from '../../protectee/backupForms'
import { Modal } from '../../ui/Modal'
import { Spinner } from '../../ui/Spinner'
import { toast } from '../../ui/notify'

/**
 * Settings → Saveenergee: `saveenergee_settings/tabList.php` + Profiles
 * (`profiles/index.php`: filele din stanga, "New profile", `editForm.php`
 * in dreapta) + Consuming (`consuming/index.php`: cele doua formulare,
 * Device consuming si Energy cost), element cu element.
 */
export function SaveenergeeSettingsPage({ tab }: { tab: string }) {
  const navigate = useNavigate()
  const shell = useQuery(saveenergeeSettingsShellQuery())

  useEffect(() => {
    if (!shell.data) return
    if (tab !== '' && isSaveenergeeSettingsTab(tab) && shell.data.tabs.some((t) => t.id === tab)) return
    const first = shell.data.tabs[0]
    if (first) void navigate({ to: '/settings/saveenergee/$tab', params: { tab: first.id } })
  }, [shell.data, tab, navigate])

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!shell.data) return <Spinner />
  const current = isSaveenergeeSettingsTab(tab) ? tab : (shell.data.tabs[0]?.id ?? '')

  return (
    <>
      <aside className="od-dark-sidebar od-module-rail">
        <ul className="od-dark-sidebar-nav default-sidebar-nav">
          <li className="menu-header">Overview</li>
          {shell.data.tabs.map((item) => (
            <SidebarTab key={item.id} tab={item} current={current} />
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </aside>
      <div className="page-content-wrapper">
        {current === 'profiles' && <ProfilesPanel />}
        {current === 'consuming' && <ConsumingPanel />}
      </div>
    </>
  )
}

function SidebarTab({ tab, current }: { tab: SaveenergeeSettingsTab; current: string }) {
  const navigate = useNavigate()
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    void navigate({ to: '/settings/saveenergee/$tab', params: { tab: tab.id } })
  }
  return (
    <li className={tab.id === current ? 'menu-tab active' : 'menu-tab'}>
      <a href={'/app/settings/saveenergee/' + tab.id} title={tab.label} onClick={onClick}>
        <i className={tab.icon} aria-hidden="true"></i> {tab.label}
      </a>
    </li>
  )
}

async function send(path: string, fields: Record<string, PostValue>, onDone?: (id?: number) => void): Promise<boolean> {
  try {
    const body = await post(path, fields)
    toast(body.message ?? 'Saved.', 'Success', 'success')
    onDone?.(body.id)
    return true
  } catch (err) {
    toast(err instanceof ApiError ? err.message : 'Something went wrong.', 'Error', 'error')
    return false
  }
}

/* ------------------------------------------------------------- profile */

function ProfilesPanel() {
  const client = useQueryClient()
  const page = useQuery(profilesQuery())
  const [open, setOpen] = useState(false)
  const [current, setCurrent] = useState<number>(0)
  const reload = () => client.invalidateQueries({ queryKey: ['settings', 'saveenergee', 'profiles'] })

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!page.data) return <Spinner />

  const create = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    void send('/profiles', formFields(e.currentTarget), (id) => { setOpen(false); if (id) setCurrent(id); void reload() })
  }

  return (
    <div id="content">
      <h3><strong>Energy save</strong> profiles</h3>
      <label className="color">Set Business Hours Profiles</label>
      <hr />
      <div className="row">
        <div className="col-md-3">
          <ul className="nav nav-metro-tabs tabs-left" id="groups-list">
            <li role="presentation" className="active" data-category="-1">
              <button className="btn btn-success" onClick={() => setOpen(true)}><i className="fas fa-plus"></i> New profile</button>
            </li>
            {page.data.profiles.map((p) => (
              <li key={p.ID} role="presentation" className={p.ID === current ? 'active' : undefined}>
                <a href="/app/settings/saveenergee/profiles" onClick={(e) => { e.preventDefault(); setCurrent(p.ID) }}>{p.profile_name}</a>
              </li>
            ))}
          </ul>
        </div>
        <div className="col-md-9">
          <div id="profileForm-container">{current > 0 ? <ProfileForm key={current} id={current} onSaved={reload} /> : null}</div>
        </div>
      </div>
      <Modal
        id="newProfileModal"
        open={open}
        onClose={() => setOpen(false)}
        title="Create profile"
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setOpen(false)}>Close</button>
            <button type="submit" form="createProfileForm" className="btn btn-success goSaveProfile"><i className="fa fa-check"></i> Save</button>
          </>
        }
      >
        <form id="createProfileForm" onSubmit={create}>
          <div className="form-group">
            <label>Profile name</label>
            <input className="form-control" type="text" name="profile_name" required defaultValue="" />
            <input value={page.data.company} name="company" type="hidden" />
          </div>
        </form>
      </Modal>
    </div>
  )
}

const IDLE_MINUTES = [1, ...Array.from({ length: 30 }, (_, i) => i * 5)]

/** `profiles/editForm.php`: zilele cu From/To, Hibernate si Power Monitor Sleep. */
function ProfileForm({ id, onSaved }: { id: number; onSaved: () => void }) {
  const client = useQueryClient()
  const q = useQuery(profileQuery(id))
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  const d: ProfileSheet = q.data
  const rowStyle = { borderBottom: '1px solid #C4DBFD', padding: 10, margin: 0 }
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    void send('/profiles/update', formFields(e.currentTarget), () => { void client.invalidateQueries({ queryKey: ['settings', 'saveenergee', 'profiles', id] }); onSaved() })
  }
  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="panel-body">
          <form className="form-horizontal device-green-settings" onSubmit={submit}>
            <input type="hidden" name="profile" value={d.profile_id} />
            <input type="hidden" name="company" value={d.company} />
            <div className="row" style={{ ...rowStyle, padding: '10px 10px 35px' }}>
              <label className="col-sm-1 control-label">Profile name</label>
              <div className="col-sm-6"><input type="text" className="form-control" name="profile_name" defaultValue={d.profile_name} /></div>
            </div>
            {d.businessHours.map((day) => (
              <div className="row" style={rowStyle} key={day.day}>
                <div className="col-sm-1"><input name={`days[${day.day}][checked]`} type="checkbox" defaultChecked={day.checked} className="icheckBusiness" /></div>
                <div className="col-sm-2 font-14">{day.dayString}</div>
                <div className="col-sm-3">From <input style={{ width: 100 }} className="day-timepicker" type="time" name={`days[${day.day}][start]`} defaultValue={day.start} /></div>
                <div className="col-sm-3">To <input style={{ width: 100 }} className="day-timepicker" type="time" name={`days[${day.day}][end]`} defaultValue={day.end} /></div>
              </div>
            ))}
            <div className="row" style={rowStyle}>
              <div className="col-sm-2 font-14">Hibernate</div>
              <div className="col-sm-3">after{' '}
                <select name="hibernate" defaultValue={String(d.hibernate_after)}>
                  {IDLE_MINUTES.map((m, i) => <option key={i} value={m}>{m}</option>)}
                </select>{' '}
                minutes in idle
              </div>
            </div>
            <div className="row" style={rowStyle}>
              <div className="col-sm-2 font-14">Power Monitor Sleep</div>
              <div className="col-sm-3">after{' '}
                <select name="monitor_sleep" defaultValue={String(d.monitor_sleep_after)}>
                  {IDLE_MINUTES.map((m, i) => <option key={i} value={m}>{m}</option>)}
                </select>{' '}
                minutes in idle
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12" style={{ marginTop: 20 }}>
                <button type="submit" className="btn btn-danger btn-sm">Update</button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------- consum */

/** `consuming/index.php`: Device consuming (Wh pe tip) si Energy cost (pret / kWh, moneda). */
function ConsumingPanel() {
  const client = useQueryClient()
  const q = useQuery(consumingQuery())
  if (q.error instanceof ApiError && q.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!q.data) return <Spinner />
  const s = q.data.company_settings
  const reload = () => client.invalidateQueries({ queryKey: ['settings', 'saveenergee', 'consuming'] })
  const field = (name: keyof typeof s, label: string) => (
    <div className="form-group">
      <label htmlFor={'consuming-' + name}>{label}</label>
      <input type="text" name={name} className="form-control" id={'consuming-' + name} defaultValue={String(s[name])} />
    </div>
  )
  return (
    <div id="content">
      <h3><strong>Consuming</strong> settings</h3>
      <label className="color">energy price settings and device consuming settings</label>
      <hr />
      <div className="row">
        <div className="col-md-6">
          <h4>Device consuming</h4>
          <form className="energyConsuming" onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); void send('/consuming', formFields(e.currentTarget), () => void reload()) }}>
            <div className="form-group"><input type="hidden" className="form-control" name="company" value={q.data.company} /></div>
            {field('desktop_consuming', 'Desktop (Wh)')}
            {field('laptop_consuming', 'Laptop (Wh)')}
            {field('server_consuming', 'Server (Wh)')}
            {field('desktop_display_consuming', 'Desktop display (Wh)')}
            {field('laptop_display_consuming', 'Laptop display (Wh)')}
            <div className="form-group"><button className="btn btn-danger">Update</button></div>
          </form>
        </div>
        <div className="col-md-6">
          <h4>Energy cost</h4>
          <form className="priceConsuming" onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); void send('/consuming/price', formFields(e.currentTarget), () => void reload()) }}>
            <div className="form-group"><input type="hidden" className="form-control" name="company" value={q.data.company} /></div>
            <div className="form-group">
              <label htmlFor="energyPrice">Price / kwh</label>
              <input type="text" className="form-control" name="consuming_price" id="energyPrice" placeholder="0.00" defaultValue={String(s.consuming_price)} />
            </div>
            <div className="form-group">
              <label htmlFor="priceCurrency">Currency</label>
              <select name="consuming_currency" className="form-control" id="priceCurrency" defaultValue={s.currency}>
                {Object.entries(q.data.currencies).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div className="form-group"><button className="btn btn-danger">Update</button></div>
          </form>
        </div>
      </div>
    </div>
  )
}
